import {
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Edit,
  CheckCircle,
  WarningAmber,
  PlaylistAdd,
  RemoveCircleOutline,
  Verified,
} from '@mui/icons-material';
import { DAY_LABELS, MIN_TAT } from './constants';

const DetailPanel = ({ rotation, theme, onRemoveLeg, onAddLeg, onValidate, onEdit, canUpdate }) => {
  const legs     = rotation.legs || [];
  const totalBlk = legs.reduce((s, l) => s + (l.blockTime || 0), 0);

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: `1px solid ${theme.palette.primary.main}`, borderRadius: 0 }}>
      {/* Header */}
      <Box sx={{ bgcolor: theme.palette.mode === 'dark' ? '#0d47a1' : '#1565c0', px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 'bold', color: '#fff' }}>
          {rotation.rotationCode}
        </Typography>
        {[rotation.acType, rotation.acReg].filter(Boolean).map((label) => (
          <Chip key={label} label={label} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '0.62rem', height: 18 }} />
        ))}
        <Chip label={`${legs.length} legs`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.62rem', height: 18 }} />
        {totalBlk > 0 && (
          <Chip
            label={`${Math.floor(totalBlk / 60)}h${String(totalBlk % 60).padStart(2, '0')}m BLK/day`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontSize: '0.6rem', height: 18 }}
          />
        )}
        {rotation.weeklyBlockHours > 0 && (
          <Chip
            label={`${rotation.weeklyBlockHours}h/wk`}
            size="small"
            sx={{ bgcolor: 'rgba(0,200,83,0.25)', color: '#b9f6ca', fontSize: '0.6rem', height: 18 }}
          />
        )}
        <Box flex={1} />
        {rotation.isValid
          ? <Chip label="VALID" size="small" icon={<CheckCircle sx={{ fontSize: '0.75rem !important', color: '#fff !important' }} />} sx={{ bgcolor: '#00c853', color: '#fff', fontSize: '0.62rem', height: 20 }} />
          : <Chip label={`${(rotation.validationIssues || []).length} ISSUES`} size="small" icon={<WarningAmber sx={{ fontSize: '0.75rem !important', color: '#fff !important' }} />} sx={{ bgcolor: '#e65100', color: '#fff', fontSize: '0.62rem', height: 20 }} />
        }
        <Tooltip title="Add legs">
          <IconButton size="small" onClick={onAddLeg} disabled={!canUpdate} sx={{ color: 'rgba(255,255,255,0.85)', ml: 0.5 }}>
            <PlaylistAdd fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Validate TAT">
          <IconButton size="small" onClick={onValidate} sx={{ color: 'rgba(255,255,255,0.85)' }}>
            <Verified fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit rotation">
          <IconButton size="small" onClick={onEdit} disabled={!canUpdate} sx={{ color: 'rgba(255,255,255,0.85)' }}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Validation issues */}
      {(rotation.validationIssues || []).length > 0 && (
        <Box sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(230,100,0,0.15)' : '#fff3e0', px: 1.5, py: 0.4, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
          {rotation.validationIssues.map((issue, i) => (
            <Typography key={i} variant="caption" display="block" color="warning.dark">⚠ {issue}</Typography>
          ))}
        </Box>
      )}

      {/* Leg table */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { fontSize: '0.7rem', fontFamily: 'monospace', py: 0.3, px: 1, whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              {['#', 'Flight', 'DEP', 'ARR', 'STD', 'STA', 'BLK', 'TAT', 'Days', ''].map((h, i) => (
                <TableCell key={i} sx={{ fontWeight: 'bold', bgcolor: `${theme.palette.background.paper} !important`, borderBottom: `2px solid ${theme.palette.divider}` }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {legs.map((leg, i) => {
              const blk   = leg.blockTime || 0;
              const tatOk = !leg.tat || leg.tat >= MIN_TAT;
              return (
                <TableRow key={i} sx={{
                  bgcolor: !tatOk
                    ? (theme.palette.mode === 'dark' ? 'rgba(229,57,53,0.1)' : 'rgba(229,57,53,0.06)')
                    : 'transparent',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                }}>
                  <TableCell sx={{ color: 'text.disabled' }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>{leg.carrier}{leg.flightNo}</TableCell>
                  <TableCell>{leg.dep}</TableCell>
                  <TableCell>{leg.arr}</TableCell>
                  <TableCell>{leg.std}</TableCell>
                  <TableCell>{leg.sta}{leg.nextDay ? <sup style={{ color: '#e65100' }}>+{leg.nextDay}</sup> : ''}</TableCell>
                  <TableCell>{blk ? `${Math.floor(blk / 60)}:${String(blk % 60).padStart(2, '00')}` : '-'}</TableCell>
                  <TableCell sx={{ color: !tatOk ? 'error.main' : leg.tat ? 'success.main' : 'text.disabled', fontWeight: !tatOk ? 'bold' : 'normal' }}>
                    {leg.tat ? `${leg.tat}m` : '-'}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.2}>
                      {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                        const on = leg.daysOfOperation?.includes(String(d));
                        return (
                          <Box key={d} sx={{
                            width: 13, height: 13, borderRadius: '2px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: on ? 'primary.main' : 'action.disabledBackground',
                            fontSize: '0.48rem', color: on ? '#fff' : 'text.disabled',
                          }}>
                            {DAY_LABELS[d][0]}
                          </Box>
                        );
                      })}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" sx={{ p: 0.15 }} onClick={() => onRemoveLeg(rotation._id, i)} disabled={!canUpdate}>
                      <RemoveCircleOutline sx={{ fontSize: '0.85rem' }} color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {legs.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                  No legs yet — click &quot;Add legs&quot; to build this rotation
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

export default DetailPanel;
